// src/languages/index.js

// English translations
export const en = {
    // Meta data
    meta: {
      title: "Lucent Web Design | Boutique Web Design Studio - Crafting Digital Excellence",
      description: "Lucent Web Design is a boutique digital studio specializing in bespoke web design and development. We create unique, high-performance websites tailored to your brand. Personal attention, innovative solutions, and transparent pricing.",
      keywords: "boutique web design agency, custom web development, professional website design, small business web design, responsive web design, UI/UX design studio, modern web development, SEO optimization, React development, creative digital agency, website redesign services, mobile-first design, web design consultation"
    },
    // Navigation
    nav: {
      home: "Home",
      services: "Services",
      portfolio: "Portfolio",
      about: "About",
      team: "Team",
      contact: "Contact"
    },
    // Hero section
    hero: {
      title: "Your Vision, Illuminated Through Design",
      subtitle: "We're a boutique web design studio that brings your digital dreams to life. With personal attention and innovative solutions, we craft websites that truly represent your brand and connect with your audience.",
      cta: "Start Your Journey",
      secondary: "Explore Our Process"
    },
    // Services section
    services: {
      title: "Tailored Solutions for Your Digital Success",
      webDesign: {
        title: "Bespoke Web Design",
        description: "Every pixel crafted with purpose. We design unique, beautiful websites that tell your story and create memorable experiences for your visitors."
      },
      development: {
        title: "Modern Development",
        description: "Clean, efficient code built with the latest technologies. We create fast, secure, and scalable websites that grow with your business."
      },
      seo: {
        title: "SEO & Optimization",
        description: "Strategic optimization to help your website rank higher and reach the right audience. We focus on sustainable, white-hat SEO practices."
      },
      ai: {
        title: "AI Integration",
        description: "Harness the power of artificial intelligence with smart chatbots, automated content generation, and predictive analytics."
      },
      security: {
        title: "Security & Performance",
        description: "Enterprise-grade security, lightning-fast load times, and 99.9% uptime guarantee. Your website protected and performant."
      },
      consulting: {
        title: "Digital Consulting",
        description: "Strategic guidance for your digital transformation. From technology selection to growth strategies, we're your trusted advisor."
      }
    },
    // Portfolio section
    portfolio: {
      title: "Our Portfolio",
      subtitle: "Check out some of our recent projects.",
      viewAll: "View All Projects",
      projects: {
        project1: {
          title: "Glücks-Couch",
          description: "Personal coaching website focused on mental wellness and life transformation.",
          tags: ["Web Design", "Personal Branding", "Coaching"]
        },
        project2: {
          title: "Hamburger Perspektivwechsel Media",
          description: "Publishing house website with online bookstore and author services.",
          tags: ["E-Commerce", "Publishing", "Content Management"]
        }
      }
    },
    // About section
    about: {
      title: "Small Studio, Big Impact",
      subtitle: "We believe in quality over quantity, creating exceptional digital experiences for select clients.",
      paragraph1: "Lucent Web Design is a passionate team of creative professionals dedicated to illuminating your brand's unique story. As a boutique studio, we take on a limited number of projects to ensure each client receives our full attention and expertise.",
      paragraph2: "We're not about mass production – we're about crafting digital masterpieces. Every project is an opportunity to push creative boundaries and deliver something truly special. When you work with us, you're not just another client; you're a partner in creating something extraordinary.",
      values: {
        quality: {
          title: "100% Custom Design",
          description: "No templates, no shortcuts. Every website we create is uniquely designed from scratch to perfectly match your brand identity."
        },
        partnership: {
          title: "Direct Communication",
          description: "Work directly with our designers and developers. No account managers, no middlemen – just transparent, personal collaboration."
        },
        innovation: {
          title: "Future-Proof Technology",
          description: "Built with modern frameworks and best practices, ensuring your website stays fast, secure, and relevant for years to come."
        }
      }
    },
    // Team section
    team: {
      title: "Meet Our Team",
      subtitle: "The creative minds behind Lucent Web Design",
      members: {
        member1: {
          name: "Leon Roese",
          role: "Lead Developer",
          bio: "Leon is our technical expert who transforms ideas into powerful web solutions."
        },
        member2: {
          name: "Burkhard Lübbers-Roese",
          role: "Finance",
          bio: "Burkhard manages our financial operations and business strategy."
        },
        member3: {
          name: "Simone Schwartz",
          role: "Concept & Design", 
          bio: "Simone creates compelling concepts and beautiful designs that bring brands to life."
        },
        member4: {
          name: "Claude",
          role: "AI Assistant",
          bio: "Claude assists with development tasks, code optimization, and technical problem-solving."
        }
      }
    },
    // Technology section
    technology: {
      title: "Future-Proof Technologies for Your Digital Success",
      subtitle: "We leverage cutting-edge technologies to build fast, secure, and scalable digital solutions.",
      items: {
        modern: {
          title: "Modern Tech Stack",
          description: "React, Next.js, TypeScript, and JAMstack architecture for blazing-fast performance and optimal SEO."
        },
        ai: {
          title: "AI-Powered Solutions",
          description: "Intelligent chatbots, personalized user experiences, and automated workflows powered by artificial intelligence."
        },
        security: {
          title: "Enterprise Security",
          description: "SSL encryption, GDPR compliance, regular security audits, and proactive threat protection."
        },
        performance: {
          title: "Lightning Performance",
          description: "Core Web Vitals optimization, CDN integration, and edge computing for sub-second load times."
        }
      }
    },
    // Contact section
    contact: {
      title: "Let's Create Something Beautiful Together",
      subtitle: "Ready to illuminate your digital presence? Schedule a free consultation and discover how we can bring your vision to life.",
      cta: "Schedule Your Consultation"
    },
    // Footer
    footer: {
      copyright: "All rights reserved.",
      socialLinks: {
        facebook: "Facebook",
        twitter: "Twitter",
        instagram: "Instagram",
        linkedin: "LinkedIn"
      },
      links: {
        privacyPolicy: "Privacy Policy",
        cookieSettings: "Cookie Settings",
        impressum: "Legal Notice"
      }
    },
    // Language selector
    language: {
      select: "Select language",
      en: "English",
      de: "Deutsch"
    }
  };
  
  // German translations
  export const de = {
    // Meta data
    meta: {
      title: "Lucent Web Design | Boutique Webdesign Studio - Digitale Exzellenz",
      description: "Lucent Web Design ist ein Boutique-Studio für maßgeschneidertes Webdesign und Entwicklung. Wir kreieren einzigartige, hochperformante Websites, die perfekt zu Ihrer Marke passen. Persönliche Betreuung, innovative Lösungen und transparente Preise.",
      keywords: "Boutique Webdesign Agentur, individuelle Webentwicklung, professionelles Website Design, Webdesign für kleine Unternehmen, responsives Webdesign, UI/UX Design Studio, moderne Webentwicklung, SEO Optimierung, React Entwicklung, kreative Digitalagentur, Website Redesign Service, Mobile-First Design, Webdesign Beratung"
    },
    // Navigation
    nav: {
      home: "Startseite",
      services: "Leistungen",
      portfolio: "Portfolio",
      about: "Über uns",
      team: "Team",
      contact: "Kontakt"
    },
    // Hero section
    hero: {
      title: "Ihre Vision, Durch Design Zum Leuchten Gebracht",
      subtitle: "Wir sind ein Boutique-Webdesign-Studio, das Ihre digitalen Träume zum Leben erweckt. Mit persönlicher Betreuung und innovativen Lösungen gestalten wir Websites, die Ihre Marke authentisch repräsentieren.",
      cta: "Ihre Reise Beginnen",
      secondary: "Unseren Prozess Entdecken"
    },
    // Services section
    services: {
      title: "Maßgeschneiderte Lösungen für Ihren Digitalen Erfolg",
      webDesign: {
        title: "Maßgeschneidertes Webdesign",
        description: "Jeder Pixel mit Bedeutung gestaltet. Wir entwerfen einzigartige, schöne Websites, die Ihre Geschichte erzählen und unvergessliche Erlebnisse schaffen."
      },
      development: {
        title: "Moderne Entwicklung",
        description: "Sauberer, effizienter Code mit neuesten Technologien. Wir erstellen schnelle, sichere und skalierbare Websites, die mit Ihrem Unternehmen wachsen."
      },
      seo: {
        title: "SEO & Optimierung",
        description: "Strategische Optimierung, damit Ihre Website höher rankt und die richtige Zielgruppe erreicht. Wir setzen auf nachhaltige, White-Hat-SEO-Praktiken."
      },
      ai: {
        title: "KI-Integration",
        description: "Nutzen Sie die Kraft künstlicher Intelligenz mit smarten Chatbots, automatisierter Content-Generierung und prädiktiven Analysen."
      },
      security: {
        title: "Sicherheit & Performance",
        description: "Enterprise-Sicherheit, blitzschnelle Ladezeiten und 99,9% Verfügbarkeitsgarantie. Ihre Website geschützt und performant."
      },
      consulting: {
        title: "Digital-Beratung",
        description: "Strategische Begleitung für Ihre digitale Transformation. Von der Technologieauswahl bis zur Wachstumsstrategie sind wir Ihr vertrauensvoller Berater."
      }
    },
    // Portfolio section
    portfolio: {
      title: "Unser Portfolio",
      subtitle: "Sehen Sie sich einige unserer neuesten Projekte an.",
      viewAll: "Alle Projekte anzeigen",
      projects: {
        project1: {
          title: "Glücks-Couch",
          description: "Personal Coaching Website für mentales Wohlbefinden und Lebenstransformation.",
          tags: ["Webdesign", "Personal Branding", "Coaching"]
        },
        project2: {
          title: "Hamburger Perspektivwechsel Media",
          description: "Verlagswebsite mit Online-Buchhandel und Autorenservices.",
          tags: ["E-Commerce", "Verlagswesen", "Content Management"]
        }
      }
    },
    // About section
    about: {
      title: "Kleines Studio, Große Wirkung",
      subtitle: "Wir glauben an Qualität statt Quantität und schaffen außergewöhnliche digitale Erlebnisse für ausgewählte Kunden.",
      paragraph1: "Lucent Web Design ist ein leidenschaftliches Team kreativer Profis, das sich darauf spezialisiert hat, die einzigartige Geschichte Ihrer Marke zum Leuchten zu bringen. Als Boutique-Studio nehmen wir nur eine begrenzte Anzahl von Projekten an, um jedem Kunden unsere volle Aufmerksamkeit zu widmen.",
      paragraph2: "Wir stehen nicht für Massenproduktion – wir erschaffen digitale Meisterwerke. Jedes Projekt ist eine Gelegenheit, kreative Grenzen zu überschreiten und etwas wirklich Besonderes zu liefern. Bei uns sind Sie nicht nur ein weiterer Kunde; Sie sind Partner bei der Erschaffung von etwas Außergewöhnlichem.",
      values: {
        quality: {
          title: "100% Individuelles Design",
          description: "Keine Vorlagen, keine Abkürzungen. Jede Website wird von Grund auf neu gestaltet, um perfekt zu Ihrer Markenidentität zu passen."
        },
        partnership: {
          title: "Direkte Kommunikation",
          description: "Arbeiten Sie direkt mit unseren Designern und Entwicklern. Keine Account Manager, keine Mittelmänner – nur transparente, persönliche Zusammenarbeit."
        },
        innovation: {
          title: "Zukunftssichere Technologie",
          description: "Mit modernen Frameworks und Best Practices entwickelt, damit Ihre Website jahrelang schnell, sicher und relevant bleibt."
        }
      }
    },
    // Team section
    team: {
      title: "Lernen Sie unser Team kennen",
      subtitle: "Die kreativen Köpfe hinter Lucent Web Design",
      members: {
        member1: {
          name: "Leon Roese",
          role: "Lead-Entwickler",
          bio: "Leon ist unser technischer Experte, der Ideen in leistungsstarke Web-Lösungen verwandelt."
        },
        member2: {
          name: "Burkhard Lübbers-Roese",
          role: "Finanzen",
          bio: "Burkhard verwaltet unsere Finanzoperationen und Geschäftsstrategie."
        },
        member3: {
          name: "Simone Schwartz",
          role: "Concept & Design", 
          bio: "Simone entwickelt überzeugende Konzepte und wunderschöne Designs, die Marken zum Leben erwecken."
        },
        member4: {
          name: "Claude",
          role: "KI-Assistent",
          bio: "Claude unterstützt bei Entwicklungsaufgaben, Code-Optimierung und technischer Problemlösung."
        }
      }
    },
    // Technology section
    technology: {
      title: "Zukunftssichere Technologien für Ihren digitalen Erfolg",
      subtitle: "Wir nutzen modernste Technologien, um schnelle, sichere und skalierbare digitale Lösungen zu entwickeln.",
      items: {
        modern: {
          title: "Moderner Tech-Stack",
          description: "React, Next.js, TypeScript und JAMstack-Architektur für blitzschnelle Performance und optimale SEO."
        },
        ai: {
          title: "KI-gestützte Lösungen",
          description: "Intelligente Chatbots, personalisierte Nutzererlebnisse und automatisierte Workflows mit künstlicher Intelligenz."
        },
        security: {
          title: "Enterprise-Sicherheit",
          description: "SSL-Verschlüsselung, DSGVO-Konformität, regelmäßige Sicherheitsaudits und proaktiver Bedrohungsschutz."
        },
        performance: {
          title: "Blitzschnelle Performance",
          description: "Core Web Vitals Optimierung, CDN-Integration und Edge Computing für Ladezeiten unter einer Sekunde."
        }
      }
    },
    // Contact section
    contact: {
      title: "Lassen Sie uns gemeinsam etwas Schönes erschaffen",
      subtitle: "Bereit, Ihre digitale Präsenz zum Leuchten zu bringen? Vereinbaren Sie eine kostenlose Beratung und entdecken Sie, wie wir Ihre Vision zum Leben erwecken können.",
      cta: "Beratungstermin vereinbaren"
    },
    // Footer
    footer: {
      copyright: "Alle Rechte vorbehalten.",
      socialLinks: {
        facebook: "Facebook",
        twitter: "Twitter",
        instagram: "Instagram",
        linkedin: "LinkedIn"
      },
      links: {
        privacyPolicy: "Datenschutzerklärung",
        cookieSettings: "Cookie-Einstellungen",
        impressum: "Impressum"
      }
    },
    // Language selector
    language: {
      select: "Sprache auswählen",
      en: "English",
      de: "Deutsch"
    }
  };
  
  // Export translations based on language code
  export const getTranslations = (languageCode) => {
    switch (languageCode) {
      case 'de':
        return de;
      case 'en':
      default:
        return en;
    }
  };